import {debug} from '../../utils/debug'
import {createQueryIndex, IndexedValues} from '../queryIndex/queryIndex'
import type {
    CoreLogger,
    DocumentDataTypes,
    FilterGetter,
    IndexKey,
    KeyValPredicate,
    Namespace,
    Null,
    NullUndefined,
    PostSetOperation,
    Transaction
} from '../../types'
import {createStore, createStoreFromJS, DalJsStore, DalValue, DmStore} from '../store'
import {createUniqueIdGenerator} from '../../utils/uniqueIdGenerator'
import {createSnapshotChain, SnapshotDal} from '../snapshot/snapshotDal'
import type {CoreConfig} from '../../documentManagerCore'
import type {EventEmitter} from 'events'
import {createValidator, type ValidatorResult} from '../validation/dalValidation'
import type {DropCommitContext, Pointer, SimpleDalAPI, ValidationService} from '@wix/document-services-types'
import _ from 'lodash'
import {basedOnSignatureNS, CustomGetter, Getter, SNAPSHOT_REMOVED_EVENT, TransactionEvent} from '../documentManagerDal'
import type {SnapshotList} from '../snapshot/snapshotList'
import type {TagManager} from '../snapshot/tagManager'
import {BaseDal} from '../baseDal'
import {PAGE_FRAGMENT_TYPE} from './fragmentDalManager'
import {ReportableError} from '@wix/document-manager-utils'
import {isConflicting as isConflictingValue} from '../signatures/isConflicting'
import {getFragmentPointer, getPointer} from '../../utils/pointerUtils'

export interface CreateFragmentArgs {
    fragmentType: string
    coreConfig: CoreConfig
    queryFilterGetters: Record<string, FilterGetter>
    postSetOperations: PostSetOperation[]
    dmTypes: DocumentDataTypes
    customGetters: Record<string, CustomGetter>
    eventEmitter: EventEmitter
    lastApproved: Null<SnapshotDal>
    snapshots: SnapshotList
    tagManager: TagManager
    customGettersEnabled: boolean
}

export interface FragmentDAL extends SimpleDalAPI {
    approve(tx: SnapshotDal): void
    hasNothingToCommit(): boolean
    commitTransactionForFragment(committer?: string, skipValidations?: boolean): DmStore | null
    find(namespace: string, indexKey: IndexKey, predicate: KeyValPredicate): any
    getIndexed(indexKey: IndexKey): IndexedValues
    getIndexKeys(indexName: string): string[]
    get(pointer: Pointer): DalValue
    has(pointer: Pointer): boolean
    isDirty(pointer: Pointer): boolean
    query(namespace: string, indexKey: IndexKey, optionalPredicate?: KeyValPredicate): Record<string, DalValue>
    queryKeys(namespace: string, indexKey: IndexKey, optionalPredicate?: KeyValPredicate): any
    getIndexPointers(indexKey: IndexKey, namespace?: string): Pointer[]
    remove(pointer: Pointer): void
    set(pointer: Pointer, value: DalValue): void
    setIfChanged(pointer: Pointer, value: DalValue): void
    touch(pointer: Pointer): void
    validate(tags?: Record<string, any>): void
    validatePendingCommit(tags?: Record<string, any>): void
    createValidationBaseline(): void
    recreateValidationBaseline(): void
    sign(
        rootPointer: Pointer,
        value: DalValue,
        signOver?: Null<SnapshotDal>
    ): {value: DalValue; basedOnSignature: NullUndefined<string>}
    getBasedOnSignatureId(namespace?: string, id?: string): string
    getBasedOnSignaturePointer(pointer: Pointer): Pointer
    setBasedOnSignature(pointer: Pointer, signature: NullUndefined<string>): void
    getCurrentOpenTransaction(): Transaction
    getAllItemsInNamespace(namespace: Namespace): Pointer[]
    mergeToApprovedStore(changes: DmStore, snapshotId: string): void
    validateAndMergeToApprovedStore(changes: DmStore, snapshotId: string): void
    dropUncommittedTransaction(reason?: string, e?: any, context?: DropCommitContext): void
    hasSignature(pointer: Pointer): boolean
    removeConflictsAndReport(event: TransactionEvent, originalChange?: DmStore): void
    updateLastApprovedSnapshot(snapshot: SnapshotDal): void
    getNamespaces(): Namespace[]
}

export class FragmentDal extends BaseDal implements FragmentDAL {
    protected debugLogger = debug(`fragment-dal-${this.fragmentType}`)
    rebaseValidator

    constructor(
        protected fragmentType: string,
        protected coreConfig: CoreConfig,
        public queryFilterGetters: Record<string, FilterGetter>,
        protected postSetOperations: PostSetOperation[],
        protected dmTypes: DocumentDataTypes,
        protected customGetters: Record<string, CustomGetter>,
        protected eventEmitter: EventEmitter,
        protected lastApproved: Null<SnapshotDal>,
        protected snapshots: SnapshotList,
        public tagManager: TagManager,
        protected customGettersEnabled: boolean
    ) {
        super(snapshots, lastApproved, customGettersEnabled)
        this.queryIndex = createQueryIndex()
        this.store = createStore()
        this.createOpenTxId = createUniqueIdGenerator(3)
        this.tentativeStore = createStore()
        this.currentOpenTransaction = this.createNewOpenTransaction()
        this.postTransactionOperations = {}
        this.updateCallbacks = []

        this.validator = createValidator(this.coreConfig, [basedOnSignatureNS])
        this.rebaseValidator = createValidator(this.coreConfig, [basedOnSignatureNS])

        this.createSignature = createUniqueIdGenerator(3, this.coreConfig.signatureSeed)

        if (this.fragmentType !== PAGE_FRAGMENT_TYPE) {
            this.validator.registerValidator(
                'validateFragmentSchema',
                createSchemaValidator(this.fragmentType, this.coreConfig.validationService, this.coreConfig.logger)
            )
        }
    }

    updateLastApprovedSnapshot(snapshot: SnapshotDal) {
        this.lastApproved = snapshot
    }

    /**
     * Merge all the changes since the last approved snapshot up to the new approved
     * snapshot into the main store and update the approved snapshot pointer.
     * Rebuild the tentative store with the remaining unapproved changes.
     * @param tx is the approved snapshot
     */
    approve(tx: SnapshotDal): void {
        const diff: DmStore = createStoreFromJS(tx.mutableDiff(this.lastApproved, this.fragmentType))
        this.store.merge(diff)

        this.tentativeStore = createStoreFromJS(this.snapshots.last()?.mutableDiff(tx, this.fragmentType) as DalJsStore)
    }

    hasNothingToCommit(): boolean {
        return this.currentOpenTransaction.store.isEmpty()
    }

    /**
     * Returns true if any of the values in the store have a signature
     * that conflicts with that of the getter value for the same pointer
     * @param transactionStore
     * @param getter
     */
    protected isConflicting(transactionStore: DmStore, getter: Getter): boolean {
        return transactionStore.some((pointer, value) => {
            const {id, type} = pointer
            const fragmentPointer = getPointer(id, type, {fragmentType: this.fragmentType})
            return (
                this.hasSignature(fragmentPointer) &&
                isConflictingValue(
                    value,
                    getter(fragmentPointer),
                    transactionStore.get(this.getBasedOnSignaturePointer(fragmentPointer))
                )
            )
        })
    }

    /**
     * Add the current open transaction to the snapshot chain and merge it to the tentative store.
     * Notify listeners of the commit
     * @param committer
     * @param skipValidations
     */
    commitTransactionForFragment(
        committer?: string | undefined,
        skipValidations?: boolean | undefined
    ): DmStore | null {
        const currentOpenTransactionStore = this.currentOpenTransaction.store
        if (currentOpenTransactionStore.isEmpty()) {
            return null
        }
        const attempt = ++this.currentOpenTransaction.attempt
        if (!skipValidations) {
            this.validatePendingCommit({committer, attempt, openTxId: this.currentOpenTransaction.id})
        }

        const pointersModifiedInTheTransaction = currentOpenTransactionStore.keys()
        this.createNewOpenTransaction()

        if (this.isConflictingWithCommittedValue(currentOpenTransactionStore)) {
            this.updateIndex(pointersModifiedInTheTransaction)
            return null
        }

        this.tentativeStore.merge(currentOpenTransactionStore)
        return currentOpenTransactionStore
    }

    mergeToApprovedStore(changes: DmStore): void {
        this.store.merge(changes)
        this.updateIndex(changes.keys())
    }

    getAllItemsInNamespace(namespace: Namespace): Pointer[] {
        const mergedNamespaceObj = this.getMergedNamespace(namespace)
        return Object.keys(mergedNamespaceObj).map(id =>
            getFragmentPointer({
                id,
                type: namespace,
                fragmentType: this.fragmentType,
                fragmentInstance: mergedNamespaceObj[id].metaData?.fragmentInstance
            })
        )
    }

    /**
     * Validate and copies changes into the dal store. Called during DS initialization
     * @param changes - the store to copy into the dal store
     * @param snapshotId - the snapshot id to use for the changes
     */
    validateAndMergeToApprovedStore(changes: DmStore, snapshotId: string): void {
        this.validateMergeToApproved(changes, snapshotId)

        this.mergeToApprovedStore(changes)
    }

    validateMergeToApproved(changes: DmStore, snapshotId: string): void {
        if (!this.tentativeStore.isEmpty() || !this.currentOpenTransaction.store.isEmpty()) {
            if (
                !changes.intersection(this.tentativeStore).isEmpty() ||
                !changes.intersection(this.currentOpenTransaction.store).isEmpty()
            ) {
                throw this.createMergeToApprovedError(changes, snapshotId)
            }
        }
    }

    /** Rebuild the tentative store with conflicting snapshots removed.
     *
     * Conflicting snapshots are snapshots with a broken signature chain to the committed value or snapshots that
     * lead to an invalid store when applied to the approved state.
     */
    removeConflicts() {
        /* To rebase we reset the dal to the approved state since it's the last "consensus" point from the server,
         * it cannot change.
         * We then re-apply the tentative transactions, which weren't approved by the server yet, one-by-one.
         * If a tentative transaction conflicts with previous values we remove it. There is no need to send it to the
         * server since we already know it's conflicting and will be rejected.
         * We refer to that situation as a "local rejection"
         */
        const openTransaction = this.currentOpenTransaction
        const droppedSnapshots: SnapshotDal[] = []
        const pendingSnapshots = createSnapshotChain(this.lastApproved, this.snapshots.last(), 'removeConflicts')
        this.resetToApprovedState()

        for (const [i, snapshot] of pendingSnapshots.entries()) {
            /* There's no need to apply the snapshot in order to check for signature conflicts.
             * But validators work on the DAL as a whole, so we have to apply the snapshot, validate and then
             * "unapply" it if a validation conflict occurred.
             * To "unapply" we rebuild the tentative store but we only use the snapshots that weren't
             * already locally rejected.
             */
            const snapshotStore = snapshot.getFragmentStore(this.fragmentType)
            if (this.isConflictingWithMergedValue(snapshotStore)) {
                droppedSnapshots.push(snapshot)
                this.removeTentativeSnapshotWithoutUpdatingTheIndex(snapshot)
            } else {
                this.currentOpenTransaction.store.merge(snapshotStore)
                this.updateIndex(snapshotStore.keys())
                if (this.hasRebaseValidationErrors(snapshotStore)) {
                    this.debugLogger.info(
                        `dmDal rebase is removing a snapshot due to validation conflict. Snapshot id: ${snapshot.id}`
                    )
                    droppedSnapshots.push(snapshot)
                    this.removeTentativeSnapshotWithoutUpdatingTheIndex(snapshot)
                    const validPendingSnapshotsUpToThisPoint = _(pendingSnapshots)
                        .take(i)
                        .without(...droppedSnapshots)
                        .value()
                    this.rebuildOpenStore(validPendingSnapshotsUpToThisPoint, this.fragmentType)
                }
            }
        }
        this.tentativeStore.merge(this.currentOpenTransaction.store)

        this.currentOpenTransaction = openTransaction
        this.updateIndex(openTransaction.store.keys())
        if (droppedSnapshots) {
            this.eventEmitter.emit(SNAPSHOT_REMOVED_EVENT, droppedSnapshots)
        }
    }

    getNamespaces(): Namespace[] {
        return Array.from(this.getMergedStore(true)._store.keys())
    }
}

export const createFragmentDal = ({
    fragmentType,
    coreConfig,
    queryFilterGetters,
    postSetOperations,
    dmTypes,
    customGetters,
    eventEmitter,
    lastApproved,
    snapshots,
    tagManager,
    customGettersEnabled
}: CreateFragmentArgs): FragmentDal => {
    return new FragmentDal(
        fragmentType,
        coreConfig,
        queryFilterGetters,
        postSetOperations,
        dmTypes,
        customGetters,
        eventEmitter,
        lastApproved,
        snapshots,
        tagManager,
        customGettersEnabled
    )
}

function createSchemaValidator(fragmentType: string, validationService?: ValidationService, logger?: CoreLogger) {
    return function (pointer: Pointer, value: DalValue): ValidatorResult[] {
        if (!value) {
            return []
        }
        const {type: namespace} = pointer
        const {type: dataType} = value
        const validator = validationService?.getFragmentValidator(fragmentType)
        if (!validator) {
            return [
                {
                    shouldFail: true,
                    type: 'missingValidator',
                    message: `Cannot find validator for schema ${dataType} in namespace ${namespace} in ${fragmentType} - validation service exists: ${!!validationService}`,
                    extras: {
                        id: pointer.id,
                        type: pointer.type,
                        fragmentType
                    }
                }
            ]
        }
        const validationResult = validator.validate(dataType, value, namespace)
        if (!validationResult.valid) {
            const error = {
                errorType: validationResult.errorType,
                message: JSON.stringify(validationResult.errors)
            }
            logger?.captureError(new ReportableError(error))
            return [
                {
                    shouldFail: true,
                    type: error.errorType,
                    message: error.message
                }
            ]
        }
        return []
    }
}
